import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { isEqual } from 'lodash'; // Import lodash for deep equality check
import { BusinessQuery } from '@/modules/dashboard/dashboardTypes';
import {
	NavigationalLeftArrow,
	XIcon,
	MapTabIcon,
	BusinessTabIcon,
	BusinessTabIconSelected,
	LocationPin
} from '@/resources/assets/images';
import { useSelector } from 'react-redux';
import './header.scss';
import type { RootState } from '@/rootReducer';
import { sendAnalytics } from '@/services/analyticsService';

interface HeaderProps {
	icon?: ReactNode;
	name?: string;
	description?: string;
	showSlider?: boolean; // Optional slider for Businesses and Map View
	navigationText?: string; // Optional navigation text prop
	listName?: string; // Optional list name
	emailCounter?: number; // Optional email counter
	totalEmails?: number; // Optional total emails
	navFunction?: () => void;
	selectedView?: 'businesses' | 'map'; // Receive selected view as a prop
	setSelectedView?: (view: 'businesses' | 'map') => void; // Function to change view
	showTooltip?: boolean;
	isMapDisabled?: boolean;
	onCloseTooltip?: () => void;
}

const HeaderComponent: FC<HeaderProps> = ({
	icon,
	name,
	description,
	showSlider,
	navigationText,
	listName,
	emailCounter,
	totalEmails,
	navFunction,
	selectedView,
	setSelectedView,
	showTooltip,
	isMapDisabled,
	onCloseTooltip
}) => {
	const query = useSelector((state: RootState) =>
		state.dashboard.get('query').toJS()
	);
	const user = useSelector((state: RootState) =>
		state.user.getIn(['authorize', 'user']).toJS()
	);

	const defaultQuery = {
		minDistance: 0,
		maxDistance: 40,
		limit: 50,
		skip: 0,
		sortField: 'score',
		sort: { score: -1 },
		count: true
	};

	const isDefaultQuery = (currentQuery: BusinessQuery) => {
		const keysToIgnore = ['sort', 'queryType', 'searchName'];
		if (currentQuery?.searches?.length === 0) {
			keysToIgnore.push('searches');
		}

		// Remove ignored keys and properties with undefined values
		const filterKeys = obj => {
			return Object.fromEntries(
				Object.entries(obj).filter(
					([key, value]) =>
						!keysToIgnore.includes(key) && value !== undefined
				)
			);
		};

		const cleanedDefault = filterKeys(defaultQuery);
		const cleanedCurrent = filterKeys(currentQuery);

		return isEqual(cleanedDefault, cleanedCurrent);
	};

	const handleViewSwitch = (view: 'businesses' | 'map') => {
		setSelectedView?.(view);
	};
	const handleCloseTooltip = () => {
		onCloseTooltip?.();

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'User dismissed Regional Map tooltip'
		});
	};

	return (
		<div
			className={`header ${
				showSlider ? 'with-slider' : 'header-no-slider'
			}`}
		>
			<div className="header-content">
				{navigationText ? (
					<>
						<button
							type="button"
							className="header-navigation"
							onClick={navFunction}
							style={{ cursor: 'pointer' }}
						>
							<div className="header-navigation-icon">
								<NavigationalLeftArrow className="rotated-arrow" />
							</div>
							<span className="navigation-text">
								{navigationText}
							</span>
						</button>
						<div className="segmentation-line" />
						<div className="header-list-info">
							<div className="list-name">{listName}</div>
						</div>
					</>
				) : (
					<>
						{icon && <div className="header-icon">{icon}</div>}

						<div className="header-text gap-4 font-barlow">
							{name && <div className="header-name">{name}</div>}

							<div
								className={
									showSlider
										? 'header-description'
										: 'header-description-no-slider'
								}
							>
								{' '}
								{description}
							</div>
						</div>
					</>
				)}
			</div>

			{showSlider && (
				<div className="header-slider-container">
					<div className="header-slider">
						<div
							className={`slider-option ${
								selectedView === 'businesses' ? 'active' : ''
							}`}
							onClick={() => {
								handleViewSwitch('businesses');
								sendAnalytics({
									type: 'track',
									userId: user.email,
									eventName: 'User clicked Business View'
								});
							}}
						>
							{selectedView === 'businesses' ? (
								<div className="businesses-selected">
									<BusinessTabIconSelected />
									Businesses
								</div>
							) : (
								<div className="businesses-unselected">
									<BusinessTabIcon />
									Businesses
								</div>
							)}
						</div>
						<div
							className={`slider-option ${
								!query?.coordinates ||
								isMapDisabled ||
								query?.state ||
								query?.country
									? 'disabled'
									: ''
							} ${selectedView === 'map' ? 'active' : ''}`}
							onClick={() => {
								if (query?.coordinates) {
									handleViewSwitch('map');
									sendAnalytics({
										type: 'track',
										userId: user.email,
										eventName: 'User clicked Map View'
									});
								}
							}}
						>
							<MapTabIcon />
							{selectedView === 'map' ? (
								<div className="businesses-selected">
									Map View
								</div>
							) : (
								<div className="businesses-unselected">
									Map View
								</div>
							)}
						</div>
					</div>
					<div
						className={`map-tooltip ${
							showTooltip &&
							!isMapDisabled &&
							!query?.state &&
							!query?.country
								? 'show-tooltip'
								: ''
						}`}
					>
						<span>Regional Map is available</span>
						<XIcon
							className="tooltip-close-icon"
							onClick={handleCloseTooltip}
						/>
						<div className="tooltip-chip-wrapper">
							<div className="tooltip-chip-border" />
							<div className="tooltip-chip" />
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default HeaderComponent;
